import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppSelector } from '@core/store/hooks';
import { logger } from '@core/utils';
import { selectActiveOrganization, selectIsImpersonating, useSwitchOrganization } from '@features/auth';

/**
 * Persistent, non-dismissible warning bar shown while a superuser is viewing
 * a tenant as a guest. Amber on purpose: it must never read as normal chrome,
 * so an impersonated session cannot be mistaken for the superuser's own.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const isImpersonating = useAppSelector(selectIsImpersonating);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const { stopImpersonating, isStoppingImpersonation } = useSwitchOrganization();

  if (!isImpersonating) return null;

  const handleStop = () => {
    stopImpersonating().catch((error) => logger.error('Failed to stop impersonation:', error));
  };

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="flex shrink-0 items-center justify-center gap-3 bg-amber-400 px-4 py-1.5 text-sm font-semibold text-amber-950"
    >
      <Icon icon="mdi:incognito" className="size-4 shrink-0" />
      <span>
        {activeOrganization?.name
          ? t('workspace.impersonationBanner.viewing', { name: activeOrganization.name })
          : t('workspace.impersonationBanner.viewingUnknown')}
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={isStoppingImpersonation}
        className="cursor-pointer rounded-md border border-amber-950/30 bg-amber-950/10 px-2.5 py-0.5 text-xs font-semibold hover:bg-amber-950/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isStoppingImpersonation ? t('workspace.impersonationBanner.stopping') : t('workspace.impersonationBanner.stop')}
      </button>
    </div>
  );
}
